<template>
  <div v-if="shouldDisplay" class="premium-concept-desc">
    <!-- Title Logo -->
    <TitleLogo :title="getConceptTitle()" :warning="false" />

    <!-- Premium Slider -->
    <div v-if="patternImages.length > 0" class="premium-slider-container">
      <!-- Pattern Selection Tabs -->
      <div class="pattern-selection-wrapper">
        <div class="concept-selection-header">
          <!-- Sparkles Icon -->
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L12 3Z" />
            <path d="M5 3v4" />
            <path d="M9 3v4" />
            <path d="M3 5h4" />
            <path d="M3 9h4" />
          </svg>
          <span class="brand-font">CONCEPT SELECTION</span>
        </div>

        <div ref="tabsContainer" class="pattern-tabs">
          <button v-for="(image, idx) in patternImages" :key="idx" @click="scrollTo(idx)" :class="['pattern-tab', { active: currentSlide === idx }]">
            <span class="pattern-prefix">PATTERN</span>
            <span class="pattern-letter">{{ String.fromCharCode(65 + idx) }}</span>
          </button>
        </div>
      </div>

      <!-- Main Slider -->
      <div class="slider-wrapper">
        <div ref="scrollContainer" class="photo-slider">
          <div v-for="(image, idx) in patternImages" :key="idx" class="photo-slide">
            <img :src="image.url" :alt="`Pattern ${String.fromCharCode(65 + idx)}`" class="photo-image" />
          </div>
        </div>

        <!-- Swipe Hint Ultra-Slim Bar -->
        <div v-if="patternImages.length > 1 && currentSlide === 0" class="swipe-hint-wrapper">
          <div class="swipe-hint-bar"></div>
        </div>
      </div>

      <!-- Pagination Indicators -->
      <div class="pagination-indicators">
        <div v-for="(_, idx) in patternImages" :key="idx" @click="scrollTo(idx)" :class="['indicator', { active: currentSlide === idx }]"></div>
      </div>
    </div>

    <!-- Storytelling Section -->
    <div v-if="descData.title" class="storytelling-section">
      <div class="glossy-card">
        <!-- Title -->
        <h2 v-if="descData.title" class="brand-font title-text" v-html="formatText(descData.title)"></h2>

        <div class="content-space">
          <!-- SubTitle -->
          <p v-if="descData.subTitle" class="subtitle-text fade-up">{{ descData.subTitle }}</p>

          <div v-if="descData.subTitle && descData.storyLine" class="divider"></div>

          <!-- StoryLine -->
          <p v-if="descData.storyLine" class="storyline-text fade-up">{{ descData.storyLine }}</p>

          <!-- Closing -->
          <div v-if="descData.closing" class="closing-box fade-up">"{{ descData.closing }}"</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex"
import TitleLogo from "@/views/components/TitleLogo"

export default {
  name: "PremiumConceptDesc",
  components: {
    TitleLogo,
  },
  props: {
    concept: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      currentSlide: 0,
      touchStartX: 0,
      touchStartY: 0,
      touchCurrentX: 0,
      touchCurrentY: 0,
      isDragging: false,
      isHorizontalSwipe: null,
      isMouseDown: false,
    }
  },

  computed: {
    ...mapState(["lang"]),

    shouldDisplay() {
      return this.concept?.productType === "premium"
    },

    descData() {
      if (!this.concept?.concept_desc) {
        return {
          title: "",
          subTitle: "",
          storyLine: "",
          closing: "",
        }
      }

      const conceptDesc = this.concept.concept_desc
      const langKey = this.lang || "kr"

      return {
        title: conceptDesc.title?.[langKey] || conceptDesc.title?.kr || "",
        subTitle: conceptDesc.subTitle?.[langKey] || conceptDesc.subTitle?.kr || "",
        storyLine: conceptDesc.storyLine?.[langKey] || conceptDesc.storyLine?.kr || "",
        closing: conceptDesc.closing?.[langKey] || conceptDesc.closing?.kr || "",
      }
    },

    patternImages() {
      const images = []

      // 기본 패턴 이미지 추가
      if (this.concept?.pattern_1_image) {
        images.push({
          url: `/upload/concept/thumbnail_image/${this.concept.pattern_1_image}`,
          desc: "",
        })
      }

      // 추가 패턴 이미지들 추가
      if (Array.isArray(this.concept?.pattern_others_image)) {
        this.concept.pattern_others_image.forEach((pattern) => {
          if (pattern.image) {
            images.push({
              url: `/upload/concept/thumbnail_image/${pattern.image}`,
              desc: "",
            })
          }
        })
      }

      return images
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.observeFadeElements()
      this.initSwipeHandlers()
    })
  },

  beforeDestroy() {
    this.removeSwipeHandlers()
  },

  methods: {
    scrollTo(index) {
      this.currentSlide = index
      if (this.$refs.scrollContainer) {
        const container = this.$refs.scrollContainer
        const slideWidth = container.offsetWidth
        const offset = -index * slideWidth

        // 모든 슬라이드에 transform 적용
        const slides = container.querySelectorAll(".photo-slide")
        slides.forEach((slide) => {
          slide.style.transform = `translateX(${offset}px)`
        })
      }

      // 탭 자동 스크롤
      this.$nextTick(() => {
        if (this.$refs.tabsContainer) {
          const tabsContainer = this.$refs.tabsContainer
          const activeTab = tabsContainer.children[index]
          if (activeTab) {
            const scrollLeft = activeTab.offsetLeft - tabsContainer.offsetWidth / 2 + activeTab.offsetWidth / 2
            tabsContainer.scrollTo({ left: scrollLeft, behavior: "smooth" })
          }
        }
      })
    },

    formatText(text) {
      if (!text) return ""
      // \n을 <br>로 변환
      return text.replace(/\n/g, "<br>")
    },

    observeFadeElements() {
      if (!this.$el || typeof this.$el.querySelectorAll !== "function") {
        return
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("visible")
            }
          })
        },
        { threshold: 0.1 }
      )

      const elements = this.$el.querySelectorAll(".fade-up")
      if (elements && elements.length > 0) {
        elements.forEach((el) => observer.observe(el))
      }
    },

    getConceptTitle() {
      if (!this.concept) return ""

      switch (this.lang) {
        case "en":
          return this.concept.title_en || this.concept.title
        case "jp":
          return this.concept.title_jp || this.concept.title
        default:
          return this.concept.title
      }
    },

    initSwipeHandlers() {
      const slider = this.$refs.scrollContainer
      if (!slider) return

      // Touch events
      slider.addEventListener("touchstart", this.handleTouchStart, { passive: true })
      slider.addEventListener("touchmove", this.handleTouchMove, { passive: false })
      slider.addEventListener("touchend", this.handleTouchEnd, { passive: true })

      // Mouse events for desktop
      slider.addEventListener("mousedown", this.handleMouseDown)
      slider.addEventListener("mousemove", this.handleMouseMove)
      slider.addEventListener("mouseup", this.handleMouseUp)
      slider.addEventListener("mouseleave", this.handleMouseLeave)
    },

    removeSwipeHandlers() {
      const slider = this.$refs.scrollContainer
      if (!slider) return

      slider.removeEventListener("touchstart", this.handleTouchStart)
      slider.removeEventListener("touchmove", this.handleTouchMove)
      slider.removeEventListener("touchend", this.handleTouchEnd)

      slider.removeEventListener("mousedown", this.handleMouseDown)
      slider.removeEventListener("mousemove", this.handleMouseMove)
      slider.removeEventListener("mouseup", this.handleMouseUp)
      slider.removeEventListener("mouseleave", this.handleMouseLeave)
    },

    handleTouchStart(e) {
      this.touchStartX = e.touches[0].clientX
      this.touchStartY = e.touches[0].clientY
      this.touchCurrentX = this.touchStartX
      this.touchCurrentY = this.touchStartY
      this.isDragging = false
      this.isHorizontalSwipe = null
    },

    handleTouchMove(e) {
      if (!this.touchStartX || !this.touchStartY) return

      this.touchCurrentX = e.touches[0].clientX
      this.touchCurrentY = e.touches[0].clientY

      const diffX = Math.abs(this.touchCurrentX - this.touchStartX)
      const diffY = Math.abs(this.touchCurrentY - this.touchStartY)

      // 방향이 아직 결정되지 않았고, 충분한 이동이 있었다면 방향 결정
      if (this.isHorizontalSwipe === null && (diffX > 10 || diffY > 10)) {
        this.isHorizontalSwipe = diffX > diffY
      }

      // Horizontal swipe인 경우에만 기본 동작 막기
      if (this.isHorizontalSwipe) {
        e.preventDefault()
        this.isDragging = true

        // 실시간으로 슬라이드 이동 (visual feedback)
        const container = this.$refs.scrollContainer
        const slideWidth = container.offsetWidth
        const offset = this.touchCurrentX - this.touchStartX
        const currentOffset = -this.currentSlide * slideWidth

        const slides = container.querySelectorAll(".photo-slide")
        slides.forEach((slide) => {
          slide.style.transition = "none"
          slide.style.transform = `translateX(${currentOffset + offset}px)`
        })
      }
      // Vertical swipe면 기본 스크롤 동작 허용
    },

    handleTouchEnd() {
      if (!this.isDragging || !this.isHorizontalSwipe) {
        this.resetTouch()
        return
      }

      const diffX = this.touchCurrentX - this.touchStartX
      const threshold = 50 // 최소 swipe 거리

      // 왼쪽으로 swipe (다음 슬라이드)
      if (diffX < -threshold && this.currentSlide < this.patternImages.length - 1) {
        this.scrollTo(this.currentSlide + 1)
      }
      // 오른쪽으로 swipe (이전 슬라이드)
      else if (diffX > threshold && this.currentSlide > 0) {
        this.scrollTo(this.currentSlide - 1)
      }
      // threshold 이하면 원위치
      else {
        this.scrollTo(this.currentSlide)
      }

      this.resetTouch()
    },

    resetTouch() {
      this.touchStartX = 0
      this.touchStartY = 0
      this.touchCurrentX = 0
      this.touchCurrentY = 0
      this.isDragging = false
      this.isHorizontalSwipe = null
      this.isMouseDown = false

      // transition 복구
      const container = this.$refs.scrollContainer
      if (container) {
        const slides = container.querySelectorAll(".photo-slide")
        slides.forEach((slide) => {
          slide.style.transition = ""
        })
      }
    },

    handleMouseDown(e) {
      this.isMouseDown = true
      this.touchStartX = e.clientX
      this.touchCurrentX = this.touchStartX
      this.isDragging = false
      e.preventDefault()
    },

    handleMouseMove(e) {
      if (!this.isMouseDown) return

      this.touchCurrentX = e.clientX
      const diffX = Math.abs(this.touchCurrentX - this.touchStartX)

      if (diffX > 5) {
        this.isDragging = true

        // 실시간으로 슬라이드 이동
        const container = this.$refs.scrollContainer
        const slideWidth = container.offsetWidth
        const offset = this.touchCurrentX - this.touchStartX
        const currentOffset = -this.currentSlide * slideWidth

        const slides = container.querySelectorAll(".photo-slide")
        slides.forEach((slide) => {
          slide.style.transition = "none"
          slide.style.transform = `translateX(${currentOffset + offset}px)`
        })
      }
    },

    handleMouseUp() {
      if (!this.isMouseDown) return

      if (this.isDragging) {
        const diffX = this.touchCurrentX - this.touchStartX
        const threshold = 50

        if (diffX < -threshold && this.currentSlide < this.patternImages.length - 1) {
          this.scrollTo(this.currentSlide + 1)
        } else if (diffX > threshold && this.currentSlide > 0) {
          this.scrollTo(this.currentSlide - 1)
        } else {
          this.scrollTo(this.currentSlide)
        }
      }

      this.resetTouch()
    },

    handleMouseLeave() {
      if (this.isMouseDown && this.isDragging) {
        this.scrollTo(this.currentSlide)
        this.resetTouch()
      }
    },
  },
}
</script>

<style scoped>
@import url("https://cdn.jsdelivr.net/gh/orioncactus/pretendard/dist/web/variable/pretendardvariable.css");

.premium-concept-desc {
  font-family: "Pretendard Variable", sans-serif;
  width: 100%;
  margin-bottom: 40px;
}

.brand-font {
  font-family: "Cafe24Danjunghae", sans-serif;
}

/* Premium Slider */
.premium-slider-container {
  width: 100%;
  max-width: 640px;
  margin: 0 auto 16px;
  padding: 0 16px;
  position: relative;
}

/* Pattern Selection */
.pattern-selection-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 2px;
  overflow: hidden;
}

.concept-selection-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  color: #dc8d9f;
  opacity: 0.8;
}

.concept-selection-header svg {
  width: 20px;
  height: 20px;
}

.concept-selection-header span {
  font-size: 10px;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  font-weight: bold;
}

.pattern-tabs {
  display: flex;
  gap: 12px;
  width: 100%;
  overflow-x: auto;
  padding: 0 16px 8px;
  scroll-behavior: smooth;
}

.pattern-tabs::-webkit-scrollbar {
  display: none;
}

.pattern-tabs {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.pattern-tab {
  flex-shrink: 0;
  padding: 10px 28px;
  border-radius: 9999px;
  transition: all 0.3s ease;
  white-space: nowrap;
  cursor: pointer;
  border: 1px solid #f3f4f6;
  background-color: #f9fafb;
  color: #9ca3af;
}

.pattern-tab:hover {
  background-color: #f3f4f6;
}

.pattern-tab.active {
  background-color: #dc8d9f;
  color: white;
  box-shadow: 0 4px 12px rgba(220, 141, 159, 0.15);
  border-color: #dc8d9f;
}

.pattern-prefix {
  font-size: 9px;
  opacity: 0.7;
  margin-right: 6px;
  vertical-align: middle;
}

.pattern-letter {
  font-size: 15px;
  font-weight: bold;
  vertical-align: middle;
}

.pattern-tab.active .pattern-letter {
  color: white;
}

.pattern-tab:not(.active) .pattern-letter {
  color: #dc8d9f;
}

/* Slider */
.slider-wrapper {
  position: relative;
}

.slider-wrapper:hover .photo-image {
  transform: scale(1.1);
}

.photo-slider {
  display: flex;
  overflow: hidden;
  position: relative;
  border-radius: 40px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  background-color: #f9fafb;
  touch-action: auto;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  cursor: grab;
}

.photo-slider:active {
  cursor: grabbing;
}

.photo-slide {
  width: 100%;
  flex-shrink: 0;
  position: relative;
  aspect-ratio: 4 / 5;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.photo-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  transition: transform 2s ease;
}

/* Swipe Hint - Ultra-Slim Bar */
.swipe-hint-wrapper {
  position: absolute;
  top: 50%;
  right: 4px;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 20;
  display: block;
}

.swipe-hint-bar {
  width: 6px;
  height: 96px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow:
    0 0 10px rgba(0, 0, 0, 0.2),
    0 0 4px rgba(0, 0, 0, 0.1);
  animation: swipeHint 2s infinite ease-in-out;
}

@keyframes swipeHint {
  0%,
  100% {
    transform: translateX(0);
    opacity: 0.5;
  }
  50% {
    transform: translateX(-4px);
    opacity: 0.9;
  }
}

/* Pagination Indicators */
.pagination-indicators {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 24px;
}

.indicator {
  height: 6px;
  width: 6px;
  border-radius: 3px;
  background-color: #ddd;
  transition: all 0.3s ease;
  cursor: pointer;
}

.indicator:hover {
  background-color: #bbb;
}

.indicator.active {
  width: 32px;
  background-color: #dc8d9f;
}

/* Storytelling Section */
.storytelling-section {
  width: 95%;
  max-width: 580px;
  margin: 0 auto;
  padding-bottom: 40px;
}

.glossy-card {
  padding: 32px;
  border-radius: 32px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

.title-text {
  font-size: 1.6rem;
  color: #dc8d9f;
  margin-bottom: 24px;
  line-height: 1.3;
}

.content-space {
  display: flex;
  flex-direction: column;
  gap: 24px;
  color: #555;
  line-height: 1.6;
  font-size: 1.05rem;
}

.subtitle-text,
.storyline-text {
  margin: 0;
}

.divider {
  width: 48px;
  height: 1px;
  background: rgba(220, 141, 159, 0.3);
  margin: 8px 0;
}

.closing-box {
  padding: 24px;
  background-color: #fdf6f8;
  border-radius: 16px;
  border: 1px solid #fceef3;
  font-size: 0.95rem;
  font-style: italic;
  color: #888;
}

/* Fade Animation */
.fade-up {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s cubic-bezier(0.22, 1, 0.36, 1);
}

.fade-up.visible {
  opacity: 1;
  transform: translateY(0);
}

/* Responsive */
@media (max-width: 768px) {
  .title-text {
    font-size: 1.3rem;
  }

  .glossy-card {
    padding: 24px;
  }

  .content-space {
    font-size: 1rem;
  }

  .concept-selection-header span {
    font-size: 9px;
  }

  .pattern-tab {
    padding: 8px 24px;
  }

  .pattern-prefix {
    font-size: 8px;
  }

  .pattern-letter {
    font-size: 13px;
  }

  .swipe-hint-wrapper {
    right: 2px;
  }

  .swipe-hint-bar {
    width: 5px;
    height: 80px;
  }
}
</style>
